import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Users, Clock, TrendingUp, Activity, Gauge, ExternalLink, AlertCircle } from 'lucide-react';
import { useAuth } from '@/hooks/useApi';
import { useTenantContext } from '@/contexts/TenantContext';
import { apiClient } from '@/lib/api';
import { useTenantBranding } from '@/hooks/useTenantBranding';
import SmsCreditsCard from '@/components/ui/SmsCreditsCard';

interface DashboardStats {
  totalCustomers: number;
  activeQueues: number;
  avgWaitTime: number;
  totalWaiting: number;
  totalCalled: number;
  totalBeingServiced: number;
  totalNoShow: number;
}

interface PlanLimits {
  limit: number;
  used: number;
  remaining: number;
  usage_percentage: number;
  plan: string;
  sms_balance?: number;
}

interface SMSData {
  totalSMS: number;
  inboundSMS: number;
  outboundSMS: number;
}

interface DashboardProps {
  onTabChange?: (tab: string) => void;
}

export function Dashboard({ onTabChange }: DashboardProps = {}) {
  const { user, isSuperAdmin, isTenantManager } = useAuth();
  const { selectedTenantId } = useTenantContext();
  useTenantBranding(); // For theme application
  const [stats, setStats] = useState<DashboardStats>({
    totalCustomers: 0,
    activeQueues: 0,
    avgWaitTime: 0,
    totalWaiting: 0,
    totalCalled: 0,
    totalBeingServiced: 0,
    totalNoShow: 0
  });
  const [planLimits, setPlanLimits] = useState<PlanLimits | null>(null);
  const [smsData, setSmsData] = useState<SMSData>({
    totalSMS: 0,
    inboundSMS: 0,
    outboundSMS: 0
  });
  const [loading, setLoading] = useState(true);
  const [trialInfo, setTrialInfo] = useState<{ is_trial: boolean; trial_end_date?: string; days_remaining?: number } | null>(null);

  const activeTenantId = selectedTenantId || user?.tenant_from_group;

  useEffect(() => {
    loadDashboardData();
    // Reduce refresh frequency to 45 seconds to leverage backend caching
    const interval = setInterval(loadDashboardData, 45000);
    return () => clearInterval(interval);
  }, [activeTenantId]);

  const loadDashboardData = async () => {
    if (!activeTenantId) {
      setLoading(false);
      return;
    }

    try {
      // Load dashboard first for immediate feedback
      const dashboardResponse = await apiClient.getDashboard(activeTenantId);
      
      // Load other data in parallel (non-blocking)
      const [limitsResponse, subscriptionResponse] = await Promise.all([
        apiClient.getPlanLimits(activeTenantId),
        apiClient.getSubscription(activeTenantId)
      ]);
      
      // Process queue data
      let totalWaiting = 0;
      let totalCalled = 0;
      let totalBeingServiced = 0;
      let totalNoShow = 0;
      let totalWaitTime = 0;
      let waitingCustomers = 0;
      
      dashboardResponse.queues.forEach(queue => {
        totalWaiting += queue.waiting.length;
        totalCalled += queue.called.length;
        totalBeingServiced += queue.being_serviced.length;
        totalNoShow += queue.no_show.length;
        
        // Calculate average wait time for waiting customers
        queue.waiting.forEach(customer => {
          totalWaitTime += customer.wait_minutes;
          waitingCustomers++;
        });
      });
      
      const avgWaitTime = waitingCustomers > 0 ? Math.round(totalWaitTime / waitingCustomers) : 0;
      const totalCustomers = totalWaiting + totalCalled + totalBeingServiced;
      
      setStats({
        totalCustomers,
        activeQueues: dashboardResponse.queues.length,
        avgWaitTime,
        totalWaiting,
        totalCalled,
        totalBeingServiced,
        totalNoShow
      });
      
      // Set plan limits and SMS data from ledger (single source of truth)
      if (limitsResponse) {
        setPlanLimits(limitsResponse);
        
        // Use ledger-based counts from plan limits response
        setSmsData({
          totalSMS: (limitsResponse.inbound || 0) + (limitsResponse.outbound || 0),
          inboundSMS: limitsResponse.inbound || 0,
          outboundSMS: limitsResponse.outbound || 0
        });
      }
      
      // Set trial info
      if (subscriptionResponse?.is_trial && subscriptionResponse?.trial_end_date) {
        const trialEndDate = new Date(subscriptionResponse.trial_end_date);
        const today = new Date();
        const daysRemaining = Math.ceil((trialEndDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
        setTrialInfo({
          is_trial: true,
          trial_end_date: subscriptionResponse.trial_end_date,
          days_remaining: daysRemaining
        });
      } else {
        setTrialInfo({ is_trial: false });
      }
      

    } catch (error) {
      console.error('Failed to load dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded w-1/4 mb-6"></div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[...Array(6)].map((_, i) => (
              <div key={i} className="h-32 bg-gray-200 rounded"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Trial Banner - Hidden for tenant_manager */}
      {!isTenantManager && trialInfo?.is_trial && trialInfo.days_remaining !== undefined && (
        <div className={`text-white p-3 sm:p-4 rounded-lg shadow-lg ${
          trialInfo.days_remaining <= 0 || (planLimits?.sms_balance !== undefined && planLimits.sms_balance <= 0)
            ? 'bg-gradient-to-r from-red-600 to-red-700'
            : 'bg-gradient-to-r from-amber-500 to-orange-600'
        }`}>
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div className="flex items-start gap-2 sm:gap-3">
              <AlertCircle className="h-5 w-5 sm:h-6 sm:w-6 flex-shrink-0 mt-0.5" />
              <div>
                {trialInfo.days_remaining <= 0 ? (
                  <>
                    <div className="font-semibold text-base sm:text-lg">
                      Trial Expired
                    </div>
                    <div className="text-xs sm:text-sm opacity-90">
                      Your trial ended on {new Date(trialInfo.trial_end_date!).toLocaleDateString()}. Upgrade now to continue using QueueMeIn.
                    </div>
                  </>
                ) : planLimits?.sms_balance !== undefined && planLimits.sms_balance <= 0 ? (
                  <>
                    <div className="font-semibold text-base sm:text-lg">
                      Out of SMS Credits
                    </div>
                    <div className="text-xs sm:text-sm opacity-90">
                      Your trial SMS credits are exhausted. Upgrade now to continue sending messages.
                    </div>
                  </>
                ) : (
                  <>
                    <div className="font-semibold text-base sm:text-lg">
                      Free Trial - {trialInfo.days_remaining} {trialInfo.days_remaining === 1 ? 'day' : 'days'} remaining
                    </div>
                    <div className="text-xs sm:text-sm opacity-90">
                      Your trial ends on {new Date(trialInfo.trial_end_date!).toLocaleDateString()}. Upgrade now to continue using QueueMeIn.
                    </div>
                  </>
                )}
              </div>
            </div>
            <button
              onClick={() => onTabChange?.('billing')}
              className="bg-white text-red-600 px-4 sm:px-6 py-2 rounded-md font-semibold hover:bg-gray-100 transition-colors text-sm sm:text-base whitespace-nowrap"
            >
              Upgrade Now
            </button>
          </div>
        </div>
      )}

      <div>
        <h1 className="text-2xl sm:text-3xl font-bold tracking-tight">Dashboard</h1>
        <div className="text-sm sm:text-base text-muted-foreground">
          Overview of your queue management system performance {isSuperAdmin && <Badge variant="outline" className="ml-2">Super Admin</Badge>}
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 md:gap-6">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Active Customers</CardTitle>
            <Users className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.totalCustomers}</div>
            <p className="text-xs text-muted-foreground">{stats.totalWaiting} waiting</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Avg Wait Time</CardTitle>
            <Clock className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.avgWaitTime}m</div>
            <p className="text-xs text-muted-foreground">Current waiting customers</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Being Served</CardTitle>
            <Activity className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.totalBeingServiced}</div>
            <p className="text-xs text-muted-foreground">{stats.totalCalled} called</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Active Queues</CardTitle>
            <TrendingUp className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.activeQueues}</div>
            <p className="text-xs text-muted-foreground">{stats.totalNoShow} no-shows</p>
          </CardContent>
        </Card>
      </div>

      {/* SMS Usage Gauge */}
      {planLimits && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Gauge className="h-5 w-5" />
              SMS Usage This Month
            </CardTitle>
            <CardDescription>
              {planLimits.plan} Plan - {(planLimits.used || 0).toLocaleString()} of {(planLimits.limit || 0).toLocaleString()} used
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              <Progress value={Math.min((planLimits.usage_percentage || 0), 100)} className="h-3" />
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">
                  {(planLimits.remaining || 0).toLocaleString()} remaining
                </span>
                <span className={(planLimits.usage_percentage || 0) > 80 ? 'text-red-600 font-medium' : 'text-muted-foreground'}>
                  {(planLimits.usage_percentage || 0).toFixed(1)}% used
                </span>
              </div>
              {(planLimits.usage_percentage || 0) > 90 && (
                <div className="text-sm text-red-600 font-medium">
                  ⚠️ Approaching SMS limit
                </div>
              )}
              <div className="grid grid-cols-2 gap-4 pt-2">
                <div className="text-center p-2 bg-blue-50 rounded">
                  <div className="text-lg font-bold text-blue-600">{smsData.inboundSMS.toLocaleString()}</div>
                  <div className="text-xs text-blue-600">Inbound</div>
                </div>
                <div className="text-center p-2 bg-green-50 rounded">
                  <div className="text-lg font-bold text-green-600">{smsData.outboundSMS.toLocaleString()}</div>
                  <div className="text-xs text-green-600">Outbound</div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6">
        <SmsCreditsCard showDetails={false} />
        
        <Card>
          <CardHeader>
            <CardTitle>Quick Actions</CardTitle>
            <CardDescription>Common management tasks</CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <button className="flex items-center justify-between p-3 border rounded hover:bg-gray-50 cursor-pointer w-full text-left" onClick={() => window.open('/live-dashboard', '_blank', 'width=1400,height=900')}>
              <div>
                <div className="font-medium">Live Dashboard</div>
                <div className="text-sm text-muted-foreground">Full-screen queue monitoring</div>
              </div>
              <ExternalLink className="h-4 w-4 text-muted-foreground" />
            </button>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>System Status</CardTitle>
            <CardDescription>AWS service health and system status</CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex items-center justify-between">
              <a 
                href="https://health.aws.amazon.com/health/status" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-sm hover:underline flex items-center gap-1"
              >
                SMS Service (SNS)
                <span className="text-xs text-gray-400">↗</span>
              </a>
              <Badge variant="default" className="bg-green-500">Online</Badge>
            </div>
            <div className="flex items-center justify-between">
              <a 
                href="https://health.aws.amazon.com/health/status" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-sm hover:underline flex items-center gap-1"
              >
                Queue Processing (Lambda)
                <span className="text-xs text-gray-400">↗</span>
              </a>
              <Badge variant="default" className="bg-green-500">Active</Badge>
            </div>
            <div className="flex items-center justify-between">
              <a 
                href="https://health.aws.amazon.com/health/status" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-sm hover:underline flex items-center gap-1"
              >
                API Gateway
                <span className="text-xs text-gray-400">↗</span>
              </a>
              <Badge variant="default" className="bg-green-500">Healthy</Badge>
            </div>
            <div className="flex items-center justify-between">
              <a 
                href="https://health.aws.amazon.com/health/status" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-sm hover:underline flex items-center gap-1"
              >
                Database (DynamoDB)
                <span className="text-xs text-gray-400">↗</span>
              </a>
              <Badge variant="default" className="bg-green-500">Online</Badge>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}